"use client";

import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  Home,
  Newspaper,
  Megaphone,
  BookOpen,
  Calendar,
  Info,
  Phone,
  Mail,
  Users,
  GraduationCap,
  Building2,
  FileText,
  Search,
  Image as ImageIcon,
  Bell,
  LayoutGrid,
  UserPlus,
  User as UserIcon,
} from "lucide-react";
import { useSafeAuth } from "@/hooks/use-safe-auth";

type IconCmp = React.ComponentType<{ size?: number; className?: string }>;

/** A resolved bottom-nav tab supplied by the server (from the
 *  `mobile-bottom` Navigation menu). `icon` is a free CMS string. */
export interface MobileNavItem {
  href: string;
  icon?: string;
  label: { en: string; mm: string };
}

interface MobileBottomNavProps {
  currentLanguage?: "en" | "mm";
  /**
   * Admin-configured tabs from the `mobile-bottom` Navigation menu
   * (resolved server-side in ThemeLayout). Empty/undefined → fall back
   * to the built-in defaults so the bar never disappears for tenants
   * that haven't configured it.
   */
  items?: MobileNavItem[];
}

/**
 * Map a CMS `icon` string to a lucide component. Normalised to
 * lowercase alphanumerics so "Book Open" / "book-open" / "bookOpen"
 * all resolve. Unknown names fall back to a neutral grid icon.
 */
const ICON_MAP: Record<string, IconCmp> = {
  home: Home,
  news: Newspaper,
  newspaper: Newspaper,
  announcement: Megaphone,
  announcements: Megaphone,
  notice: Megaphone,
  notices: Megaphone,
  megaphone: Megaphone,
  bell: Bell,
  library: BookOpen,
  book: BookOpen,
  bookopen: BookOpen,
  event: Calendar,
  events: Calendar,
  calendar: Calendar,
  about: Info,
  info: Info,
  contact: Phone,
  phone: Phone,
  mail: Mail,
  email: Mail,
  staff: Users,
  faculty: Users,
  users: Users,
  people: Users,
  student: GraduationCap,
  students: GraduationCap,
  graduation: GraduationCap,
  graduationcap: GraduationCap,
  department: Building2,
  departments: Building2,
  building: Building2,
  page: FileText,
  file: FileText,
  document: FileText,
  search: Search,
  gallery: ImageIcon,
  image: ImageIcon,
  photo: ImageIcon,
};

function resolveIcon(name?: string): IconCmp {
  if (!name) return LayoutGrid;
  const key = name.toLowerCase().replace(/[^a-z0-9]/g, "");
  return ICON_MAP[key] ?? LayoutGrid;
}

/**
 * Mobile-app-style bottom tab bar for um1sf.
 *
 * Fixed to the bottom of the viewport on screens < lg. Hidden on
 * desktop where the regular header nav is used. Renders 4 tabs:
 * Home / News / Announcements / Library — the most common
 * destinations for an everyday visitor. The active tab is
 * highlighted based on the current pathname.
 *
 * Safe-area inset bottom padding handles iOS home-bar overlap;
 * `ThemeLayout` adds `pb-16` (or equivalent) to main on mobile so
 * page content isn't covered.
 */
type Tab = {
  href: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: { en: string; mm: string };
  // Active match: exact pathname OR pathname startsWith(matchPrefix).
  matchPrefix?: string;
};

const TABS: Tab[] = [
  {
    href: "/",
    icon: Home,
    label: { en: "Home", mm: "ပင်မ" },
  },
  {
    href: "/post/news",
    icon: Newspaper,
    label: { en: "News", mm: "သတင်း" },
    matchPrefix: "/post/news",
  },
  {
    href: "/post/announcements",
    icon: Megaphone,
    label: { en: "Notices", mm: "ကြေညာချက်များ" },
    matchPrefix: "/post/announcements",
  },
  {
    href: "/library",
    icon: BookOpen,
    label: { en: "Library", mm: "စာကြည့်တိုက်" },
    matchPrefix: "/library",
  },
];

/** Normalised tab the bar actually renders (icon already resolved). */
type RenderTab = {
  href: string;
  Icon: IconCmp;
  label: { en: string; mm: string };
  matchPrefix?: string;
};

function isActive(
  pathname: string,
  tab: { href: string; matchPrefix?: string },
): boolean {
  if (tab.href === "/" && pathname === "/") return true;
  const prefix = tab.matchPrefix ?? (tab.href !== "/" ? tab.href : undefined);
  if (
    prefix &&
    (pathname === prefix || pathname.startsWith(prefix + "/"))
  ) {
    return true;
  }
  return false;
}

export function MobileBottomNav({
  currentLanguage = "en",
  items,
}: MobileBottomNavProps) {
  const pathname = usePathname() || "/";
  const lang = currentLanguage === "mm" ? "mm" : "en";
  // The last slot is auth-aware. During the probe we optimistically treat the
  // visitor as a guest (show Sign up) — signup is the priority CTA and this
  // nav is persistent, so the probe runs once per page load.
  const { isAuthenticated } = useSafeAuth();

  // Admin-configured items win; otherwise fall back to the built-in defaults.
  // Cap at 4 so the auth slot appended below keeps the bar at 5 tabs — usable
  // on narrow screens.
  const navTabs: RenderTab[] =
    items && items.length > 0
      ? items.slice(0, 4).map((it) => ({
          href: it.href || "/",
          Icon: resolveIcon(it.icon),
          label: it.label,
        }))
      : TABS.slice(0, 4).map((t) => ({
          href: t.href,
          Icon: t.icon,
          label: t.label,
          matchPrefix: t.matchPrefix,
        }));

  // Far-end auth slot: Profile when signed in, Sign up otherwise.
  const authTab: RenderTab = isAuthenticated
    ? {
        href: "/profile",
        Icon: UserIcon,
        label: { en: "Profile", mm: "ပရိုဖိုင်" },
        matchPrefix: "/profile",
      }
    : {
        href: "/signup",
        Icon: UserPlus,
        label: { en: "Sign up", mm: "အကောင့်ဖွင့်" },
        matchPrefix: "/signup",
      };

  const resolved: RenderTab[] = [...navTabs, authTab];

  return (
    <nav
      aria-label="Primary mobile navigation"
      // Fixed bottom, full-width, hidden on lg+ screens (which use
      // the regular header). Backdrop blur keeps it readable over
      // scrolled content; a soft upward shadow lifts it off the page.
      // iOS safe-area padding via env(safe-area).
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/95 backdrop-blur shadow-[0_-6px_24px_-12px_rgba(0,0,0,0.25)] supports-[backdrop-filter]:bg-background/85"
      style={{
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <ul className="flex items-stretch justify-around px-1 h-16">
        {resolved.map((tab) => {
          const active = isActive(pathname, tab);
          const Icon = tab.Icon;
          return (
            <li key={tab.href} className="flex-1">
              <Link
                href={tab.href}
                aria-current={active ? "page" : undefined}
                className="group relative flex h-full w-full flex-col items-center justify-center gap-1 px-1 transition-transform active:scale-95"
              >
                {/* Active top accent — a short primary bar hugging the
                    nav's top edge, centred over the tab. */}
                <span
                  aria-hidden
                  className={`absolute top-0 left-1/2 h-0.5 w-8 -translate-x-1/2 rounded-full transition-opacity ${
                    active ? "opacity-100" : "opacity-0"
                  }`}
                  style={{ backgroundColor: "var(--color-primary)" }}
                />
                {/* Icon in a rounded pill — tinted primary when active,
                    a light hover wash otherwise (Material/iOS feel). */}
                <span
                  className={`flex h-8 w-14 items-center justify-center rounded-full transition-colors ${
                    active ? "bg-primary/10" : "group-hover:bg-muted/70"
                  }`}
                >
                  <Icon
                    size={20}
                    className={
                      active
                        ? "text-primary stroke-[2.25]"
                        : "text-muted-foreground stroke-[1.75] group-hover:text-foreground"
                    }
                  />
                </span>
                <span
                  className={`text-[10px] leading-none tracking-tight ${
                    active
                      ? "font-semibold text-primary"
                      : "font-medium text-muted-foreground"
                  }`}
                >
                  {tab.label[lang]}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export default MobileBottomNav;
